// src/lib/errors/ApiError.ts
import axios from 'axios';

export interface ApiErrorInit {
  status: number;
  message: string;
  code?: string;
  fieldErrors?: Record<string, string>;
}

/**
 * Normalized API error. Every query/mutation error handler should assume
 * this shape; services/hooks throw or reject with this class.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly fieldErrors?: Record<string, string>;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.fieldErrors = init.fieldErrors;
  }
}

interface ErrorEnvelope {
  code?: unknown;
  message?: unknown;
  fieldErrors?: unknown;
}

function isErrorEnvelope(v: unknown): v is ErrorEnvelope {
  return typeof v === 'object' && v !== null;
}

function pickString(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

function pickFieldErrors(v: unknown): Record<string, string> | undefined {
  if (!isErrorEnvelope(v)) return undefined;
  const out: Record<string, string> = {};
  for (const [k, val] of Object.entries(v)) {
    if (typeof val === 'string') out[k] = val;
  }
  return Object.keys(out).length ? out : undefined;
}

function pickFieldErrorsFromArray(v: unknown): Record<string, string> | undefined {
  if (!Array.isArray(v)) return undefined;
  const out: Record<string, string> = {};
  for (const entry of v) {
    if (typeof entry !== 'string') continue;
    const idx = entry.indexOf(':');
    if (idx <= 0) continue;
    const field = entry.slice(0, idx).trim();
    const message = entry.slice(idx + 1).trim();
    if (field && message) out[field] = message;
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Normalize any thrown value into an ApiError.
 * Backend convention: `{ error: { code, message, fieldErrors? } }` on 4xx/5xx.
 */
export function normalizeError(raw: unknown): ApiError {
  if (raw instanceof ApiError) return raw;

  if (axios.isAxiosError(raw)) {
    const response = raw.response;
    if (!response) {
      return new ApiError({
        status: 0,
        code: 'NETWORK',
        message: raw.message || 'Network error',
      });
    }
    const body = response.data as {
      error?: ErrorEnvelope;
      code?: unknown;
      message?: unknown;
      fieldErrors?: unknown;
      errors?: unknown;
    } | null;
    // Two accepted shapes:
    //   (a) nested:    { error: { code, message, fieldErrors? } }
    //   (b) top-level: { status: 'error', code?, message, fieldErrors? }
    const envelope = body?.error;
    return new ApiError({
      status: response.status,
      code: pickString(envelope?.code) ?? pickString(body?.code) ?? 'UNKNOWN',
      message:
        pickString(envelope?.message) ??
        pickString(body?.message) ??
        (raw.message || `HTTP ${String(response.status)}`),
      fieldErrors:
        pickFieldErrors(envelope?.fieldErrors) ??
        pickFieldErrors(body?.fieldErrors) ??
        pickFieldErrorsFromArray(body?.errors),
    });
  }

  if (raw instanceof Error) {
    return new ApiError({ status: 0, code: 'UNKNOWN', message: raw.message });
  }

  return new ApiError({ status: 0, code: 'UNKNOWN', message: `Unknown error: ${String(raw)}` });
}
